<script setup lang="ts">
import { computed } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Button from '@/components/ui/UIButton.vue';
import Badge from '@/components/ui/Badge.vue';
import Separator from '@/components/ui/Separator.vue';
import { Play, Clock, Brain, Loader2, CheckCircle2, AlertCircle } from '@lucide/vue';
import type { Document } from '@/types/ui/PromptManager';
import type { ModelInvocationStatus } from '@/types/ai';

interface Props {
  canExecute: boolean;
  isExecuting: boolean;
  selectedDocument: Document | null;
  selectedModel: string;
  promptContent: string;
  error?: string;
  invocationId?: string | null;
  invocationStatus?: ModelInvocationStatus | null;
}

const props = defineProps<Props>();

defineEmits<{
  execute: [];
}>();

const executionStatus = computed(() => {
  if (props.invocationStatus === 'FAILED') return 'failed';
  if (props.invocationStatus === 'COMPLETED') return 'completed';
  if (props.invocationStatus === 'RUNNING') return 'running';
  if (props.invocationStatus === 'PENDING') return 'pending';
  if (props.isExecuting) return 'executing';
  // if (!props.selectedDocument) return 'no-document';
  if (!props.selectedModel) return 'no-model';
  if (!props.promptContent) return 'no-prompt';
  return 'ready';
});

const statusMessage = computed(() => {
  switch (executionStatus.value) {
    case 'pending':
      return 'Invocation queued, waiting to start...';
    case 'running':
      return 'Processing document with AI model...';
    case 'completed':
      return 'Execution completed successfully';
    case 'failed':
      return 'Execution failed';
    case 'executing':
      return 'Processing document with AI model...';
    // case 'no-document':
    //   return 'Please select a document to process';
    case 'no-prompt':
      return 'Please select or create a prompt';
    case 'no-model':
      return 'Please select a model';
    case 'ready':
      return 'Ready to execute prompt';
    default:
      return '';
  }
});

const statusColor = computed(() => {
  switch (executionStatus.value) {
    case 'pending':
      return 'text-yellow-600';
    case 'running':
    case 'executing':
      return 'text-blue-600';
    case 'completed':
      return 'text-green-600';
    case 'failed':
      return 'text-red-600';
    // case 'no-document':
    case 'no-prompt':
      return 'text-amber-600';
    case 'ready':
      return 'text-green-600';
    default:
      return 'text-slate-600';
  }
});
</script>

<template>
  <Card class="border-slate-200 bg-white/80 backdrop-blur-sm">
    <CardHeader>
      <CardTitle class="flex items-center gap-2">
        <Play class="h-5 w-5 text-green-600" />
        Execution
      </CardTitle>
      <CardDescription> Run prompts against selected documents </CardDescription>
    </CardHeader>
    <CardContent class="space-y-4">
      <!-- Status -->
      <div class="rounded-lg border bg-slate-50 p-4">
        <div class="mb-3 flex items-center gap-2">
          <Loader2 v-if="executionStatus === 'running' || executionStatus === 'executing'" class="h-4 w-4 animate-spin text-blue-600" />
          <Clock v-else-if="executionStatus === 'pending'" class="h-4 w-4 animate-pulse text-yellow-600" />
          <CheckCircle2 v-else-if="executionStatus === 'completed'" class="h-4 w-4 text-green-600" />
          <AlertCircle v-else-if="executionStatus === 'failed'" class="h-4 w-4 text-red-600" />
          <Clock v-else class="h-4 w-4 text-slate-500" />
          <span :class="['text-sm font-medium', statusColor]">
            {{ statusMessage }}
          </span>
        </div>
        <div v-if="invocationId" class="mb-2 text-xs text-slate-500">
          Invocation ID: {{ invocationId }}
        </div>

        <!-- Execution Details -->
        <div class="space-y-3">
          <!--TODO: commented out until the document processing is complete -->
          <!-- <div class="flex items-center justify-between"> -->
          <!--   <span class="text-sm text-slate-600">Document:</span> -->
          <!--   <Badge v-if="selectedDocument" variant="outline"> -->
          <!--     <FileText class="w-3 h-3 mr-1" /> -->
          <!--     {{ selectedDocument.fileName }} -->
          <!--   </Badge> -->
          <!--   <Badge v-else variant="outline">Not selected</Badge> -->
          <!-- </div> -->

          <Separator />

          <div class="flex items-center justify-between">
            <span class="text-sm text-slate-600">Prompt:</span>
            <Badge v-if="promptContent" variant="outline">
              <Brain class="mr-1 h-3 w-3" />
              {{ promptContent.length }} characters
            </Badge>
            <Badge v-else variant="outline">Not selected</Badge>
          </div>

          <template v-if="executionStatus === 'pending' || executionStatus === 'running' || executionStatus === 'executing'">
            <Separator />
            <div class="flex items-center justify-between">
              <span class="text-sm text-slate-600">Progress:</span>
              <div class="flex items-center gap-2">
                <div class="h-2 w-24 rounded-full bg-slate-200">
                  <div 
                    :class="[
                      'h-2 rounded-full',
                      executionStatus === 'pending' ? 'animate-pulse bg-yellow-600' : 'animate-pulse bg-blue-600'
                    ]" 
                    :style="executionStatus === 'pending' ? 'width: 30%' : 'width: 60%'"></div>
                </div>
                <span class="text-xs text-slate-500">
                  {{ executionStatus === 'pending' ? 'Queued...' : 'Processing...' }}
                </span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Execute Button -->
      <Button
        @click="$emit('execute')"
        :disabled="!canExecute"
        class="w-full bg-linear-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-400"
        size="lg">
        <template v-if="isExecuting">
          <Loader2 class="mr-2 h-4 w-4 animate-spin" />
          Executing Prompt...
        </template>
        <template v-else>
          <Play class="mr-2 h-4 w-4" />
          Execute Prompt
        </template>
      </Button>

      <!-- Error Message -->
      <div v-if="error" class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
        <p class="font-medium">Error:</p>
        <p class="mt-1">{{ error }}</p>
      </div>

      <!-- Execution Info -->
      <div class="space-y-1 text-xs text-slate-500">
        <p>• Execution typically takes 5-10 seconds</p>
        <p>• Results will appear in JSON format</p>
        <p>• Large documents may take longer to process</p>
      </div>
    </CardContent>
  </Card>
</template>
